import { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CheckIcon, CopyIcon } from './icons';

/** How long the "copied" feedback stays before the live region empties again. */
const COPIED_MS = 2500;

type CopyState = 'idle' | 'copied' | 'failed';

interface ReferenceLineProps {
  requestId: string;
  /** `center` under a page-level error state; `start` everywhere else. */
  align?: 'center' | 'start';
  /**
   * `secondary` (`text.secondary`) on a plain surface; `inherit` inside an Alert, whose own ink is
   * the guaranteed pair on its soft fill (TMMAPP-0122 item 23).
   */
  ink?: 'secondary' | 'inherit';
}

/**
 * A failure's support reference (TMM-B3): "Reference: {requestId}" in mono (an identifier,
 * TMM-T1), a copy button, and a polite status for the copy. The error state shows it under its body,
 * and so does any charge refusal that names a reference (TMMAPP-0122 item 23).
 */
export function ReferenceLine({
  requestId,
  align = 'start',
  ink = 'secondary',
}: ReferenceLineProps) {
  const { t } = useTranslation();
  const color = ink === 'inherit' ? 'inherit' : 'text.secondary';
  const [copy, setCopy] = useState<CopyState>('idle');
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (copyTimer.current !== null) clearTimeout(copyTimer.current);
    },
    [],
  );

  async function copyReference() {
    let next: CopyState = 'copied';
    try {
      await navigator.clipboard.writeText(requestId);
    } catch {
      // No Clipboard API (an insecure context, an old browser) or permission denied: the
      // reference is still on screen to select, and the live region says so.
      next = 'failed';
    }
    setCopy(next);
    if (copyTimer.current !== null) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopy('idle'), COPIED_MS);
  }

  const copyMessage =
    copy === 'copied'
      ? t('errors.referenceCopied')
      : copy === 'failed'
        ? t('errors.copyFailed')
        : '';

  return (
    <Box sx={{ minWidth: 0, maxWidth: '100%' }} data-reference-line="">
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: align === 'center' ? 'center' : 'flex-start',
        }}
      >
        <Typography variant="mono" component="span" sx={{ color, overflowWrap: 'anywhere' }}>
          {t('errors.reference', { requestId })}
        </Typography>
        <IconButton
          size="small"
          color="inherit"
          aria-label={t('errors.copyReference')}
          onClick={() => void copyReference()}
        >
          {copy === 'copied' ? <CheckIcon size={20} /> : <CopyIcon size={20} />}
        </IconButton>
        <Typography variant="caption" role="status" sx={{ color }}>
          {copyMessage}
        </Typography>
      </Stack>
    </Box>
  );
}
